/* eslint-disable */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ECharts from 'echarts-for-react';
import { Box, Tab, Table, TableBody, TableCell, TableContainer, TableRow, Tabs } from "@mui/material";
import { getdDiagnosisResultChainGraphDayByQuery } from "components/axios/equipmentAlertsAxios";
import MainCard from "components/MainCard";
import EquipmentPartSensorsGraphsLayer from "components/layer/EquipmentPartSensorsGraphsLayer";
// import dayjs from "utils/dayjs";
import usePolling from "./usePolling";
import useConfig from "hooks/useConfig";
import { fromServerFormat } from "components/date_picker/dateUtils";
import { isSameData } from "utils/graphUtils";

const DELAY_TIME = Number(process.env.REACT_APP_DELAY_TIME) || 60000;
// const DELAY_TIME =  60000;

function EquipmentPartChainGraphs({equipment, equipmentPartId, diagnosisResultChain, type="none", target='default'}) {
    const { t } = useTranslation();
    const { startPolling, stopPolling } = usePolling();

    const { mode } = useConfig();
    const textColor = mode === 'light' ? '#262626' : '#ffffff';
    const bgColor = mode === 'light' ? '#f9f9fa' : '#2f2f2f';
// console.log(equipmentPartId)
    const chartDataInit = () => ({
        // darkmode:true,
        textStyle : {
            color : textColor
        },
        tooltip: {
        trigger: 'axis'
        },
        legend: {
            data : [],
            left : 'center',
            top: 0,
            backgroundColor : bgColor,
            textStyle: {
                color: textColor
            }
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
        },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: [ ]
        },
        yAxis: {
            type: 'value'
        },
        series: [ ]
    })

    // initial chart options only, real-time updates via setOption
    const lengthOptions = useRef(chartDataInit());
    const elongOptions = useRef(chartDataInit());

    const [value, setValue] = useState(0);
    const [isVisible, setIsVisible] = useState(
        typeof document !== 'undefined' ? !document.hidden : true
    );
    const [uiReady, setUiReady] = useState({
        lengths: false,
        elongs: false,
    });

    const lengthChartRef = useRef(null);
    const elongChartRef = useRef(null);

    const pollingRef = useRef(null);
    // const elongPollingRef = useRef(null);

    const prevLengthDataRef = useRef(null);
    const buildLengthData = (list) => ({
        x: list.map(v => v.ymd),
        lengths: list.map(v => Number(v.length.toFixed(2))),
    });
    const prevElongDataRef = useRef(null);
    const buildElongData = (list) => ({
        x: list.map(v => v.ymd),
        elongs: list.map(v => Number(v.elongation.toFixed(2))),
    });

    const clearChart = (chartRef) => {
        const chart = chartRef.current?.getEchartsInstance();
        if (!chart) return;
        chart.clear();
    };

    const updateLengthChart = (data) => {
        const chart = lengthChartRef.current?.getEchartsInstance();
        if (!chart) return;

        chart.setOption({
            xAxis: { data: data.x },
            series: [
                { 
                    name: t('message.common.subtitle.length'),
                    type: 'line',
                    lineStyle : {width:5, type:'solid'},
                    symbolSize : 8,
                    data: data.lengths
                },
            ],
            legend: {
                data: [
                    t('message.common.subtitle.length'),
                ],
            },
        }, false); // ⭐ merge
    };

    const updateElongChart = (data) => {
        const chart = elongChartRef.current?.getEchartsInstance();
        if (!chart) return;

        chart.setOption({
            xAxis: { data: data.x },
            series: [
                { 
                    name: t('message.common.subtitle.elongation'),
                    type: 'line',
                    lineStyle : {width:5, type:'solid'},
                    symbolSize : 8,
                    data: data.elongs
                },
            ],
            legend: {
                data: [
                    t('message.common.subtitle.elongation'),
                ],
            },
        }, false);
    };

    useEffect(() => {
        setUiReady({
            lengths: false,
            elongs: false,
        });

        prevLengthDataRef.current = null;
        prevElongDataRef.current = null;

        clearChart(lengthChartRef);
        clearChart(elongChartRef);

        setValue(0);
    }, [equipmentPartId]);

    useEffect(() => {
        const handleVisibility = () => {
            setIsVisible(!document.hidden);
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, []);

    useEffect(() => {
        if (value === 0 && prevLengthDataRef.current) {
            updateLengthChart(prevLengthDataRef.current);
        }
        if (value === 1 && prevElongDataRef.current) {
            updateElongChart(prevElongDataRef.current);
        }
        
    }, [value]);

    useEffect(()=>{
        if(!equipmentPartId || !isVisible)  {
            stopPolling(pollingRef)
            return;
        }
        let graphParam = {"equipmentPart.id":equipmentPartId};
        const fetchLengthAndElong = async () => {
            getdDiagnosisResultChainGraphDayByQuery(graphParam).then((data)=>{
                if (data && data._embedded) {
                    let list = data._embedded.groupByDailyDiagnosisResultChains;
                    list.sort((a, b) => a.ymd.localeCompare(b.ymd));

                    const nextLengthData = buildLengthData(list);
                    const nextElongData = buildElongData(list);

                    // 변경 없으면 종료
                    if (isSameData(prevLengthDataRef.current, nextLengthData) 
                        && isSameData(prevElongDataRef.current, nextElongData)) {
                        return;
                    }
                    if (!isSameData(prevLengthDataRef.current, nextLengthData)) {
                        prevLengthDataRef.current = nextLengthData;
                        setUiReady(prev => ({ ...prev, lengths: true }));
                        // if (!uiReady.lengths) {
                        //     setUiReady(prev => ({ ...prev, lengths: true }));
                        // }
                        updateLengthChart(nextLengthData);
                    }
                    if (!isSameData(prevElongDataRef.current, nextElongData)) {
                        prevElongDataRef.current = nextElongData;
                        setUiReady(prev => ({ ...prev, elongs: true }));
                        // if (!uiReady.elongs) {
                        //     setUiReady(prev => ({ ...prev, elongs: true }));
                        // }
                        updateElongChart(nextElongData);
                    }
                    
                } else {
                    clearChart(lengthChartRef);
                    clearChart(elongChartRef);
                }
            })
        };

        fetchLengthAndElong();
        startPolling(pollingRef, fetchLengthAndElong, DELAY_TIME);

        return () => stopPolling(pollingRef);

    },[equipmentPartId, isVisible]);

    useEffect(() => {
        if (!uiReady.lengths) return;
        if (!prevLengthDataRef.current) return;
        if (prevLengthDataRef.current)
            updateLengthChart(prevLengthDataRef.current);
    }, [uiReady.lengths]);

    useEffect(() => {
        if (!uiReady.elongs) return;
        if (!prevElongDataRef.current) return;

        updateElongChart(prevElongDataRef.current);
    }, [uiReady.elongs]);

    // useEffect(()=>{
    //     if (lengthOptions) {
    //         setValue(0)
    //     } else if (elongOptions) {
    //         setValue(1)
    //     }
    // },[lengthOptions, elongOptions]);

    const applyTheme = (chartRef) => {
        const chart = chartRef.current?.getEchartsInstance();
        if (!chart) return;

        chart.setOption(
            {
                textStyle: { color: textColor },
                legend: {
                    backgroundColor: bgColor,
                    textStyle: { color: textColor },
                },
            },
            false,
            true
        );
    };

    useEffect(() => {
        applyTheme(lengthChartRef);
        applyTheme(elongChartRef);
    }, [mode]);

   const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    // console.log(uiReady.lengths , uiReady.elongs) 
    // console.log(uiReady.lengths && uiReady.elongs) 

    if (uiReady.lengths && uiReady.elongs) {
        let className = '';
        if (type === 'popup') className='pop_tr';
        return (
        <>
            {diagnosisResultChain && 
            <TableContainer>
                <Table sx={{ minWidth: 500 }} aria-label="simple table" className="same_width">
                    <TableBody>
                        <TableRow className={className} sx={{borderBottom: "1px solid rgba(81, 81, 81, 1)"}}>
                            <TableCell className="th" align="center" style={{width:'90px'}}>{t('message.common.subtitle.diagnosis.date')}</TableCell>
                            <TableCell align="center" style={{width:'120px'}}>{diagnosisResultChain.analysis_at && fromServerFormat(diagnosisResultChain.analysis_at,{format:t('message.common.text.date.format.date')})}</TableCell>
                            <TableCell className="th" align="center" style={{width:'90px'}}>{t('message.common.subtitle.diagnosis.result')}</TableCell>
                            <TableCell align="center" colSpan={`${target === 'default'?'3':'1'}`}>{`${t('message.common.subtitle.length')} : ${diagnosisResultChain.length.toFixed(2)}, ${t('message.common.subtitle.elongation')} : ${diagnosisResultChain.elongation.toFixed(2)}`}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            }
            <div className="row">
                <div className="mixed-chart">
                    <Box>
                        <Tabs value={value} onChange={handleChange} style={{paddingTop:"10px", paddingLeft:"10px"}} sx={{ ".Mui-selected":{bgcolor:"#0a2e60"} }}>
                            {/* {lengthOptions && <Tab value={0} label={t('message.common.chip.length.trend.graph')} className="graphTab" />}
                            {elongOptions && <Tab value={1} label={t('message.common.chip.elongation.trend.graph')} className="graphTab" />} */}

                            {uiReady.lengths && <Tab value={0} 
                                className="graphTab"
                                style={{border:`${value === 0 ? "3px solid #0061d0": "1px solid #0061d0"}`}}
                                label={
                                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                                {t('message.common.chip.length.trend.graph')}
                                &nbsp;&nbsp;
                                <EquipmentPartSensorsGraphsLayer equipment={equipment} sensorPosition={null} equipmentPartId={equipmentPartId} 
                                    equipmentPartPositionName={null} equipmentPartName={null} type="SL" />
                                </Box>
                                } />}
                            {uiReady.elongs && <Tab value={1} 
                                className="graphTab"
                                style={{border:`${value === 1 ? "3px solid #0061d0": "1px solid #0061d0"}`}}
                                label={
                                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                                {t('message.common.chip.elongation.trend.graph')}
                                &nbsp;&nbsp;
                                <EquipmentPartSensorsGraphsLayer equipment={equipment} sensorPosition={null} equipmentPartId={equipmentPartId} 
                                    equipmentPartPositionName={null} equipmentPartName={null} type="SE" />
                                </Box>
                                } />}

                            
                        </Tabs>
                        <Box mt={2}>
                            {value === 0 && uiReady.lengths &&
                                <MainCard content={false}>
                                    <ECharts
                                        ref={lengthChartRef}
                                        option={lengthOptions.current}
                                        opts={{ renderer: 'svg', height: '300px' }}
                                      />
                                </MainCard>
                            }
                            {value === 1 && uiReady.elongs &&
                                <MainCard content={false}>
                                    <ECharts
                                        ref={elongChartRef}
                                        option={elongOptions.current}
                                        opts={{ renderer: 'svg', height: '300px' }}
                                    />
                                </MainCard>
                            }
                        </Box>
                    </Box>
                </div>
            </div>
            <div className="divide" />
        </>
        )
    }
}

export default EquipmentPartChainGraphs;